import { Addon } from '@galaxy/marketplace-apps/v1';
import { ResellerItem } from '@vendasta/store';
import { MarketplaceApp } from '../marketplace-app/marketplace-app';
import { Product } from '../product/product';
import { ResellerItemMap } from './reseller-item-map';

export function convertAppToResellerItem(app: MarketplaceApp): ResellerItem {
  if (!app) {
    return undefined;
  }
  return new ResellerItem({
    appId: app.appId,
    addonId: undefined,
    type: app.type,
    name: app.name,
    icon: app.icon,
    tagline: app.tagline,
    headerImage: app.headerImage,
    currency: app.currency,
    price: app.price ? Number(app.price) : 0,
    setupFee: app.setupFee ? Number(app.setupFee) : 0,
    billingFrequency: (app.billingFrequency || '').toLowerCase(),
    lmiCategories: (app.lmiCategories || []).map((lmi) => (lmi || '').toLowerCase()),
    enabled: false, // the existence of a product model means this is enabled.
    isCustom: false,
    quantity: 1, // :(
    orderForm: {
      orderForm: app.orderForm,
      commonForm: app.commonForm,
      activationMessage: app.activationMessage,
      separateOrderForms: false,
    },
    allowMultipleActivations: app.allowMultipleActivations || false,
    usesCustomizationForm: app.usesOrderForm,
    usesActivationSpecificEntryUrl: app.usesActivationSpecificEntryUrl,
    entryUrl: undefined, // not on the internal app model
    restrictions: app.restrictions,
    activationRequiresApproval: app.activationRequiresApproval,
    billingId: app.billingId,
    usesEditions: app.usesEditions,
    editionIds: app.editionIds,
    suggestedRetailPrice: app.suggestedRetailPrice,
    trialConfiguration: app.trialConfiguration,
    integration: app.integration,
    suspended: app.suspended,
    usesCustomPricing: app.usesCustomPricing,
  });
}

export function convertAddonToResellerItem(addon: Addon, parentApp: ResellerItem | MarketplaceApp): ResellerItem {
  if (!addon) {
    return undefined;
  }
  const ri = new ResellerItem({
    appId: addon.appId,
    addonId: addon.addonId,
    name: addon.title,
    icon: addon.icon,
    tagline: addon.tagline,
    headerImage: addon.bannerImage,
    price: Number(addon.price),
    setupFee: addon.setupFee || 0,
    usesCustomPricing: addon.usesCustomPricing,
    billingFrequency: (addon.billingFrequency || '').toLowerCase(),
    enabled: undefined, // enablement determine by whether its parent is enabled or not
    isCustom: false,
    quantity: 1,
    usesCustomizationForm: addon.usesOrderForm,
    orderForm: addon.orderForm,
    allowMultipleActivations: addon.multipleActivations,
    restrictions: addon.restrictions,
    activationRequiresApproval: addon.activationRequiresApproval,
    billingId: addon.billingId,
    currency: addon.currency,
  });

  if (parentApp != null) {
    ri.lmiCategories = parentApp.lmiCategories;
  }

  return ri;
}

function isCustomProduct(product: Product): boolean {
  if (!product) {
    return false;
  }
  return product.product_id?.startsWith('CP-') || product.origin === 'partner_created';
}

export function convertProductToResellerItem(product: Product): ResellerItem {
  if (!product) {
    return undefined;
  }
  return new ResellerItem({
    appId: product.product_id,
    addonId: undefined, // only apps can be products
    name: product.name,
    icon: product.icon_url,
    tagline: product.tagline,
    headerImage: product.header_image_url,
    price: Number(product.wholesale_price),
    setupFee: product.setup_fee || 0,
    currency: product.currency,
    suggestedRetailPrice: product.recommended_sell_price,
    billingFrequency: (product.billing_frequency || '').toLowerCase(),
    enabled: true, // products are the enabled versions of apps
    isCustom: isCustomProduct(product),
    quantity: 1,
    usesCustomizationForm: product.uses_customization_form,
    usesActivationSpecificEntryUrl: product.uses_activation_specific_entry_url,
    orderForm: {
      orderForm: product.order_form,
      commonForm: undefined,
      activationMessage: undefined,
      separateOrderForms: false,
    },
    allowMultipleActivations: false,
    restrictions: product.restrictions,
    activationRequiresApproval: undefined,
    billingId: product.billing_id || product.product_id,
  });
}

export function convertCustomProductToResellerItem(cp: Product): ResellerItem {
  if (!cp) {
    return undefined;
  }
  return new ResellerItem({
    appId: cp.product_id,
    addonId: undefined, // only apps can be products
    name: cp.name,
    icon: cp.icon_url,
    tagline: cp.tagline,
    headerImage: cp.header_image_url,
    price: Number(cp.wholesale_price) || 0,
    setupFee: cp.setup_fee || 0,
    currency: cp.currency,
    billingFrequency: (cp.billing_frequency || '').toLowerCase(),
    enabled: true, // products are the enabled versions of apps
    isCustom: true,
    quantity: 1,
    usesCustomizationForm: cp.uses_customization_form,
    // Order forms depends on the usesActivationSpecificEntry URL field to show the 'custom entry url'`. For current custom products the field uses_customization_form meant that.
    // for migrated products, for migrated custom product into marketplace apps, the resulting product will have the uses_activation_specific field set
    usesActivationSpecificEntryUrl: cp.uses_customization_form || cp.uses_activation_specific_entry_url,
    orderForm: {
      orderForm: cp.order_form,
      commonForm: undefined,
      activationMessage: undefined,
      separateOrderForms: false,
    },
    allowMultipleActivations: false,
    restrictions: cp.restrictions,
    activationRequiresApproval: undefined,
    billingId: cp.billing_id || cp.product_id,
  });
}

export function makeResellerMap(
  products: ResellerItem[],
  customProducts: ResellerItem[],
  addons: ResellerItem[],
  apps: ResellerItem[],
): ResellerItemMap {
  const array = customProducts.concat(addons, products, apps);
  return array.reduce((resellerItemMap, current) => {
    if (current.addonId) {
      resellerItemMap[current.addonId] = current.name;
    } else {
      // Prioritize products and custom products before apps
      if (!resellerItemMap[current.appId]) {
        resellerItemMap[current.appId] = current.name;
      }
    }
    return resellerItemMap;
  }, {} as ResellerItemMap) as ResellerItemMap;
}
